export type Location = {
  id: string;
  name: string;
  address: string | null;
  addressLink: string | null;
  phone: string | null;
  logoUrl: string | null;
  timezone: string;
};

export type AppointmentTemplate = {
  id: string;
  name: string;
  /** The practice's approved wording of the name and instructions by locale; English where none. */
  nameTranslations: Partial<Record<string, string>>;
  durationMinutes: number;
  requireNotes: boolean;
  /** Practice-written subtext shown under this type in the picker, or null when there is none. */
  bookingInstructions: string | null;
  bookingInstructionsTranslations: Partial<Record<string, string>>;
  /** Deposit due to book this type, or null when this location takes none. */
  depositAmountCents: number | null;
};

export type AppointmentTemplates = {
  newPatient: AppointmentTemplate[];
  existingPatient: AppointmentTemplate[];
};

export type Provider = {
  id: string;
  name: string;
};

export type TimeSlot = {
  key: string;
  label: string;
  startTime: string;
  providerName: string | null;
};

/** "How did you hear about us?" on the new-patient step (OnlineScheduling::ReferralQuestion). */
export type ReferralQuestion = {
  /** The intake policy requires an answer and the patient didn't come from an ad. */
  required: boolean;
  /** "Other" needs a description. */
  detailRequired: boolean;
  /** The stored values of Other and of the choices whose detail names who referred the patient. */
  otherValue: string;
  whoValues: string[];
  /** The stored English value and its wording in each language the page offers, English included. */
  options: { value: string; labels: Partial<Record<string, string>> & { en: string } }[];
};

export type PatientType = "new_patient" | "existing_patient";

export type Step =
  | "patient_type"
  | "appointment_info"
  | "provider_select"
  | "appointment_time"
  | "patient_info"
  | "existing_patient_info"
  | "insurance_info"
  | "review"
  // After review on purpose: nothing about the booking can change once the
  // deposit is paid, so the amount quoted cannot drift from the amount charged.
  | "deposit"
  | "confirmation";

/** The answer the insurance step turns on: a new patient has insurance, an existing one has new insurance. */
export function insuranceAnswer(formData: FormData): "yes" | "no" | null {
  return formData.patientType === "existing_patient"
    ? formData.insuranceChanged
    : formData.hasInsurance;
}

export type UploadedCard = {
  signedId: string;
  /** An object URL of the picked file, for the thumbnail. */
  previewUrl: string;
};

export type FormData = {
  patientType: PatientType | null;
  appointmentTemplateId: string | null;
  chiefComplaint: string;
  providerId: string | null;
  appointmentDate: string;
  appointmentSlotKey: string | null;
  appointmentSlotProviderName: string | null;
  /** Our reservation on the chosen slot, taken when the patient commits to it. */
  holdId: string | null;
  waitlist: boolean;
  firstName: string;
  lastName: string;
  preferredName: string;
  dob: string;
  phone: string;
  email: string;
  referredFromSelection: string;
  referredFromDetail: string;
  hasInsurance: "yes" | "no" | null;
  /** An existing patient's answer to "has your insurance changed?"; Yes opens the same intake. */
  insuranceChanged: "yes" | "no" | null;
  insuranceName: string;
  insuranceSubscriberName: string;
  /** Typed as the date input gives it, YYYY-MM-DD. */
  insuranceSubscriberDob: string;
  insuranceSubscriberId: string;
  insuranceGroupNumber: string;
  /** Card photos stored as the patient picked them; the booking names them by signed id. */
  insuranceCardFront: UploadedCard | null;
  insuranceCardBack: UploadedCard | null;
  phoneVerificationCode: string;
  phoneVerificationId: string;
  /** Numbers sent a code / verified this visit. Held here, not in the step, so Back keeps them. */
  codeSentPhones: string[];
  verifiedPhones: string[];
  marketingClickId: string | null;
  /** The patient picked the page's language on its menu, so the booking records it as their choice. */
  languagePicked: boolean;
};

export type ConfirmationData = {
  providerName: string | null;
  startTime: string | null;
  endTime: string | null;
  appointmentId: string;
};
